import axios, { type AxiosInstance } from 'axios'
import { isTokenValid, isTokenExpired } from '@/utils/jwt'

// Base URL del backend (solo origen, ej. https://tu-api.railway.app). Las rutas ya incluyen /api/...
// Vite solo lee .env al iniciar: si cambias .env, reinicia "npm run dev".
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'

// Crear instancia de axios
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para requests
apiClient.interceptors.request.use(
  (config) => {
    // Si los datos son FormData, no establecer Content-Type
    // El navegador lo establecerá automáticamente con el boundary correcto
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    // Agregar token de autenticación si existe y es válido
    const token = localStorage.getItem('token')
    if (token) {
      // Verificar que el token sea válido antes de agregarlo
      const isAuthRequest = config.url?.includes('/auth/')
      
      if (!isAuthRequest) {
        // Para peticiones no de autenticación, verificar el token
        if (!isTokenValid(token) || isTokenExpired(token)) {
          // Token inválido o expirado, limpiar
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          // Rechazar la petición
          return Promise.reject(new Error('Token expired or invalid'))
        }
      }
      
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para responses
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Manejo de errores global
    if (error.response) {
      // El servidor respondió con un código de estado fuera del rango 2xx
      const status = error.response.status
      const requestUrl = error.config?.url || ''
      
      // Si el token es inválido o expiró, limpiar y redirigir al login
      // PERO solo si NO es una petición de autenticación (signin/signup)
      if (status === 401) {
        const isAuthRequest = requestUrl.includes('/auth/signin') || requestUrl.includes('/auth/signup')
        
        if (!isAuthRequest) {
          // Solo limpiar y redirigir si NO es una petición de login/registro
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          // Redirigir al login solo si no estamos ya en una ruta pública
          if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/signup')) {
            window.location.href = '/'
          }
        }
        // Si es una petición de autenticación, solo rechazar el error sin redirigir
        // para que el componente pueda manejar el error
      }
      
    } else if (error.request) {
      // La petición fue hecha pero no se recibió respuesta
    } else {
      // Algo pasó al configurar la petición
    }
    return Promise.reject(error)
  }
)

export default apiClient

